/**
 * The page locale for structured data, read from the URL (CWS-45).
 *
 * Its own module rather than an export of ``JsonLd.tsx``: that file exports
 * components, and Vite's fast refresh — and the
 * ``react-refresh/only-export-components`` lint rule — want a component module
 * to export nothing else. Both JSON-LD modules import it from here.
 */

import { useLocation } from "react-router-dom";
import { normalizeSeoLanguage, type SeoLanguage } from "@/lib/seoMetadata";

/**
 * Read the locale of the page a JSON-LD block is emitted on, from its URL.
 *
 * Every wrapper in `JsonLd.tsx` and `PageJsonLd.tsx` takes its locale from
 * here, **not** from i18n's current language (CWS-45). The `<head>` writer in
 * `LanguageRouter` already reads the path, and the two used to agree only
 * because `LanguageRouter` syncs i18n on mount and `src/main.tsx` awaits
 * `initializeLanguage()` before the first render. Measured: with i18n on `en`
 * and no router sync, a `/de/industries/pharma` breadcrumb emitted
 * `https://shelpuk.com/en/industries/pharma`.
 *
 * **What this guarantees, and what it does not.** Every locale-prefixed URL and
 * every `inLanguage` in structured data follows the page's URL, as the
 * canonical and `hreflang` do. It does **not** make the whole page consistent
 * under a divergence: names, descriptions and FAQ copy still come from `t()`,
 * and so does the visible body, so a page whose i18n disagrees with its URL is
 * wrong whatever the markup says. The remedy for that is preventing the
 * divergence — `LanguageRouter`'s sync, the path-first order
 * `languageResolution.test.ts` pins, `main.tsx`'s await, and CWS-57 for
 * prerendering — not this hook. See `SYSTEM_DESIGN.md` §13a.1.
 *
 * @returns The first path segment, normalised through
 *     :func:`normalizeSeoLanguage`, so an unprefixed or unsupported path yields
 *     the fallback locale.
 */
export function usePageLocale(): SeoLanguage {
  const { pathname } = useLocation();
  return normalizeSeoLanguage(pathname.split("/").filter(Boolean)[0]);
}
